import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import {
  Shield,
  FileText,
  Database,
  GraduationCap,
  Search,
  Map,
  MessageSquare,
  GitBranch,
  FileSignature,
  Mail,
  Phone,
  MapPin,
  CheckCircle,
  ArrowRight,
  Menu,
  X,
  Star,
  Calendar,
  Clock,
  Award,
  Target,
  Briefcase,
  LifeBuoy,
  DollarSign,
  MessageCircle,
  Send,
  Download,
  FileCheck,
  Cookie,
  TrendingUp,
  ShieldCheck,
  FileSearch,
  ExternalLink,
  Settings,
  Video,
  Globe,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import axios from 'axios';
import {
  companyInfo,
  heroData,
  aboutData,
  servicesData,
  caseStudiesData,
  whyChooseUsData,
  blogData,
  testimonialsData,
  projectDeliverablesData,
  founderData
} from '../mockData';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Shield,
  FileText,
  Database,
  GraduationCap,
  Search,
  Map,
  MessageSquare,
  GitBranch,
  FileSignature,
  Award,
  Target,
  Briefcase,
  LifeBuoy,
  DollarSign,
  Clock,
  FileCheck,
  Cookie,
  TrendingUp,
  ShieldCheck,
  FileSearch,
  Settings,
  Globe,
  AlertTriangle,
  CheckCircle
};

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogLoading, setBlogLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const navigate = useNavigate();

  // Fetch blog posts from API
  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setBlogPosts(response.data);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        // Fallback to mock data if API fails
        setBlogPosts(blogData);
      } finally {
        setBlogLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['about', 'founder', 'services', 'why-choose', 'case-studies', 'testimonials', 'projects', 'resources', 'contact'];
      const scrollPosition = window.scrollY + 150; // Offset for navbar height

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Restore scroll position from sessionStorage
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('scrollPosition');
    if (savedScrollPosition) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScrollPosition));
        sessionStorage.removeItem('scrollPosition');
      }, 100);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      toast({
        title: "Message Sent Successfully!",
        description: "Thank you for reaching out. We'll get back to you within 24 hours.",
      });
      
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0e1a] via-[#0d1525] to-[#0a0e1a] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#0a0e1a]/95 backdrop-blur-md border-b border-cyan-500/10 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
          <div className="flex items-center space-x-3">
          <div className="relative flex flex-col items-start">
            <img
              src="/logo_Lexprive.png"
              alt="Lexprive Consultants"
              className="
                h-8
                object-contain
                scale-115
                sm:scale-100
                md:scale-150
                origin-left"
            />

            {/* <p className="text-gray-400 text-sm mt-1">
                {companyInfo.tagline}
            </p> */}
           </div>
           </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-6 text-sm">
              <button 
                onClick={() => scrollToSection('about')} 
                className={`transition-colors font-medium ${
                  activeSection === 'about' 
                    ? 'text-cyan-400' 
                    : 'text-gray-300 hover:text-cyan-400'
                }`}
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection('founder')} 
                className={`transition-colors font-medium ${
                  activeSection === 'founder' 
                    ? 'text-cyan-400' 
                    : 'text-gray-300 hover:text-cyan-400'
                }`}
              >
                Founder
              </button>
              <button 
                onClick={() => scrollToSection('services')} 
                className={`transition-colors font-medium ${
                  activeSection === 'services' 
                    ? 'text-cyan-400' 
                    : 'text-gray-300 hover:text-cyan-400'
                }`}
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('case-studies')} 
                className={`transition-colors font-medium ${
                  activeSection === 'case-studies' 
                    ? 'text-cyan-400' 
                    : 'text-gray-300 hover:text-cyan-400'
                }`}
              >
                Case Studies
              </button>
              <button 
                onClick={() => scrollToSection('testimonials')} 
                className={`transition-colors font-medium ${
                  activeSection === 'testimonials' 
                    ? 'text-cyan-400' 
                    : 'text-gray-300 hover:text-cyan-400'
                }`}
              >
                Testimonials
              </button>
              <button 
                onClick={() => scrollToSection('projects')} 
                className={`transition-colors font-medium ${
                  activeSection === 'projects' 
                    ? 'text-cyan-400' 
                    : 'text-gray-300 hover:text-cyan-400'
                }`}
              >
                Projects
              </button>
              <button 
                onClick={() => scrollToSection('resources')} 
                className={`transition-colors font-medium ${
                  activeSection === 'resources' 
                    ? 'text-cyan-400' 
                    : 'text-gray-300 hover:text-cyan-400'
                }`}
              >
                Resources
              </button>
              <button 
                onClick={() => scrollToSection('contact')} 
                className={`transition-colors font-medium ${
                  activeSection === 'contact' 
                    ? 'text-cyan-400' 
                    : 'text-gray-300 hover:text-cyan-400'
                }`}
              >
                Contact
              </button>
            </div>

            <div className="hidden md:block">
              <Button 
                onClick={() => scrollToSection('contact')} 
                className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white border-0 shadow-lg shadow-cyan-500/50"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-300"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0d1525] border-t border-cyan-500/10">
            <div className="px-4 pt-2 pb-4 space-y-2">
              <button 
                onClick={() => scrollToSection('about')} 
                className={`block w-full text-left px-4 py-2 rounded transition ${
                  activeSection === 'about' 
                    ? 'bg-cyan-500/20 text-cyan-400' 
                    : 'text-gray-300 hover:bg-cyan-500/10 hover:text-cyan-400'
                }`}
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection('founder')} 
                className={`block w-full text-left px-4 py-2 rounded transition ${
                  activeSection === 'founder' 
                    ? 'bg-cyan-500/20 text-cyan-400' 
                    : 'text-gray-300 hover:bg-cyan-500/10 hover:text-cyan-400'
                }`}
              >
                Founder
              </button>
              <button 
                onClick={() => scrollToSection('services')} 
                className={`block w-full text-left px-4 py-2 rounded transition ${
                  activeSection === 'services' 
                    ? 'bg-cyan-500/20 text-cyan-400' 
                    : 'text-gray-300 hover:bg-cyan-500/10 hover:text-cyan-400'
                }`}
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('why-choose')} 
                className={`block w-full text-left px-4 py-2 rounded transition ${
                  activeSection === 'why-choose' 
                    ? 'bg-cyan-500/20 text-cyan-400' 
                    : 'text-gray-300 hover:bg-cyan-500/10 hover:text-cyan-400'
                }`}
              >
                Why Choose Us
              </button>
              <button 
                onClick={() => scrollToSection('case-studies')} 
                className={`block w-full text-left px-4 py-2 rounded transition ${
                  activeSection === 'case-studies' 
                    ? 'bg-cyan-500/20 text-cyan-400' 
                    : 'text-gray-300 hover:bg-cyan-500/10 hover:text-cyan-400'
                }`}
              >
                Case Studies
              </button>
              <button 
                onClick={() => scrollToSection('testimonials')} 
                className={`block w-full text-left px-4 py-2 rounded transition ${
                  activeSection === 'testimonials' 
                    ? 'bg-cyan-500/20 text-cyan-400' 
                    : 'text-gray-300 hover:bg-cyan-500/10 hover:text-cyan-400'
                }`}
              >
                Testimonials
              </button>
              <button 
                onClick={() => scrollToSection('projects')} 
                className={`block w-full text-left px-4 py-2 rounded transition ${
                  activeSection === 'projects' 
                    ? 'bg-cyan-500/20 text-cyan-400' 
                    : 'text-gray-300 hover:bg-cyan-500/10 hover:text-cyan-400'
                }`}
              >
                Projects
              </button>
              <button 
                onClick={() => scrollToSection('resources')} 
                className={`block w-full text-left px-4 py-2 rounded transition ${
                  activeSection === 'resources' 
                    ? 'bg-cyan-500/20 text-cyan-400' 
                    : 'text-gray-300 hover:bg-cyan-500/10 hover:text-cyan-400'
                }`}
              >
                Resources
              </button>
              <button 
                onClick={() => scrollToSection('contact')} 
                className={`block w-full text-left px-4 py-2 rounded transition ${
                  activeSection === 'contact' 
                    ? 'bg-cyan-500/20 text-cyan-400' 
                    : 'text-gray-300 hover:bg-cyan-500/10 hover:text-cyan-400'
                }`}
              >
                Contact
              </button>
              <Button 
                onClick={() => scrollToSection('contact')} 
                className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white mt-2"
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-transparent"></div>
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-white via-cyan-200 to-white bg-clip-text text-transparent">
                {heroData.headline}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8">
              {heroData.subheadline}
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 mb-10">
              {heroData.highlights.map((highlight, index) => (
                <div key={index} className="flex items-center gap-2 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 px-4 py-2 rounded-full border border-cyan-500/20">
                  <CheckCircle className="h-5 w-5 text-cyan-400" />
                  <span className="text-gray-300 font-medium">{highlight}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => scrollToSection('contact')} 
                size="lg"
                className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white text-lg px-8 border-0 shadow-lg shadow-cyan-500/50"
              >
                Start Your Compliance Journey
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
  onClick={() => navigate("/book-consultation")}
  size="lg"
  variant="outline"
  className="border-2 border-cyan-500 text-cyan-400 hover:bg-cyan-500/100 text-lg px-8"
>
  <Video className="mr-2 h-5 w-5" />
  Online Consultation
</Button>

            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent"></div>
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <Badge className="mb-4 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border-cyan-500/30">
            About Us
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">
            {aboutData.title}
          </h2>
          <p className="text-lg text-gray-300 mb-6 leading-relaxed">{aboutData.description}</p>
          <p className="text-lg text-gray-300 mb-8 leading-relaxed">{aboutData.mission}</p>
          
          <div className="space-y-4 max-w-2xl mx-auto">
            {aboutData.expertise.map((item, index) => (
              <div key={index} className="flex items-start gap-3 group justify-center text-left">
                <div className="relative">
                  <CheckCircle className="h-6 w-6 text-cyan-400 relative z-10" />
                  <div className="absolute inset-0 blur-md bg-cyan-400/30 group-hover:bg-cyan-400/50 transition"></div>
                </div>
                <p className="text-gray-300">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section id="founder" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#0a0e1a] to-[#0d1525]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border-cyan-500/30">
              Leadership
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">
              Meet Our Founder
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Column - Photo */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-2xl blur-2xl"></div>
              <Card className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] border-cyan-500/20 overflow-hidden relative z-10">
                <CardContent className="p-0">
                  <div className="aspect-square relative bg-gradient-to-br from-cyan-500/10 to-purple-500/10 flex items-center justify-center">
                    <div className="text-center p-8">
                      <div className="h-48 w-48 mx-auto bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-full flex items-center justify-center mb-6 border-4 border-cyan-500/30">
                        <span className="text-6xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">HA</span>
                      </div>
                      <p className="text-sm text-gray-400 italic">Professional photo coming soon</p>
                    </div>
                  </div>
                  <div className="p-6 border-t border-cyan-500/20">
                    <h3 className="text-2xl font-bold text-white mb-2">{founderData.name}</h3>
                    <p className="text-cyan-400 mb-4">{founderData.title}</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-400">
                        <GraduationCap className="h-4 w-4 text-cyan-400" />
                        <span>{founderData.education}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <Award className="h-4 w-4 text-cyan-400" />
                        <span>{founderData.certification}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <Briefcase className="h-4 w-4 text-cyan-400" />
                        <span>{founderData.experience} Experience</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Bio */}
            <div className="space-y-6">
              <Card className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] border-cyan-500/20">
                <CardHeader>
                  <CardTitle className="text-white">Professional Background</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    {founderData.bio}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] border-cyan-500/20">
                <CardHeader>
                  <CardTitle className="text-cyan-400">{founderData.expertise.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-300 leading-relaxed">
                    {founderData.expertise.description}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] border-cyan-500/20">
                <CardHeader>
                  <CardTitle className="text-white">Areas of Expertise</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {founderData.specializations.map((spec, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{spec}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border-cyan-500/30">
              Our Services
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">Our Services</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Comprehensive DPDPA compliance solutions tailored to your business needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {servicesData.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 group hover:shadow-xl hover:shadow-cyan-500/20">
                  <CardHeader>
                    <div className="relative w-fit mb-4">
                      <div className="h-14 w-14 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-xl flex items-center justify-center relative z-10 group-hover:scale-110 transition">
                        <IconComponent className="h-7 w-7 text-cyan-400" />
                      </div>
                      <div className="absolute inset-0 blur-lg bg-cyan-500/30 group-hover:bg-cyan-500/50 transition"></div>
                    </div>
                    <CardTitle className="text-xl text-white group-hover:text-cyan-400 transition">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="why-choose" className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border-cyan-500/30">
              Why Choose Us
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">Why Choose Lexprive</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Partner with India's trusted data protection experts
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUsData.map((item, index) => {
              const IconComponent = iconMap[item.icon];
              return (
                <div key={index} className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500/50 transition-all group hover:shadow-xl hover:shadow-purple-500/20">
                  <div className="relative w-fit mb-4">
                    <div className="h-12 w-12 bg-gradient-to-br from-purple-500/20 to-cyan-500/20 rounded-lg flex items-center justify-center relative z-10">
                      <IconComponent className="h-6 w-6 text-purple-400" />
                    </div>
                    <div className="absolute inset-0 blur-md bg-purple-500/30 group-hover:bg-purple-500/50 transition"></div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-purple-400 transition">{item.title}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border-cyan-500/30">
              Case Studies
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">Case Studies</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Real results for real businesses across India
            </p>
          </div>

          <div className="space-y-8">
            {caseStudiesData.map((study) => (
              <Card key={study.id} className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] border-cyan-500/20 hover:border-cyan-500/50 transition overflow-hidden">
                <CardContent className="p-8">
                  <div className="grid md:grid-cols-3 gap-8">
                    <div>
                      <Badge className="mb-3 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 text-purple-400 border-purple-500/30">
                        {study.industry}
                      </Badge>
                      <h3 className="text-2xl font-bold text-white mb-2">{study.client}</h3>
                      <div className="flex flex-wrap gap-2 mt-4">
                        {study.tools.map((tool, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs text-cyan-400 border-cyan-500/30">
                            {tool}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div className="md:col-span-2 space-y-4">
                      <div>
                        <h4 className="font-semibold text-cyan-400 mb-2">Challenge</h4>
                        <p className="text-gray-400">{study.challenge}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-cyan-400 mb-2">Solution</h4>
                        <p className="text-gray-400">{study.solution}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-cyan-400 mb-2">Results</h4>
                        <p className="text-cyan-300 font-medium">{study.results}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border-cyan-500/30">
              Testimonials
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">Client Testimonials</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Trusted by leading businesses across India
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonialsData.map((testimonial) => (
              <Card key={testimonial.id} className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] border-cyan-500/20 hover:border-cyan-500/50 transition">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-cyan-400 text-cyan-400" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-6 italic leading-relaxed">"{testimonial.content}"</p>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12 bg-gradient-to-br from-cyan-500 to-purple-500">
                      <AvatarFallback className="bg-transparent text-white font-bold">{testimonial.avatar}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-white">{testimonial.name}</p>
                      <p className="text-sm text-gray-400">{testimonial.position}</p>
                      {testimonial.company && (
                        <p className="text-xs text-cyan-400 mt-1">{testimonial.company}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Project Deliverables Section */}
      <section id="projects" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border-cyan-500/30">
              Project Portfolio
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">Project Deliverables</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Comprehensive DPDPA compliance projects demonstrating our expertise across industries
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {projectDeliverablesData.map((project) => {
              const IconComponent = iconMap[project.icon];
              return (
                <Card key={project.id} className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] border-cyan-500/20 hover:border-cyan-500/50 transition-all duration-300 group">
                  <CardHeader>
                    <div className="flex items-start justify-between mb-4">
                      <div className="relative w-fit">
                        <div className="h-14 w-14 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-xl flex items-center justify-center relative z-10 group-hover:scale-110 transition">
                          <IconComponent className="h-7 w-7 text-cyan-400" />
                        </div>
                        <div className="absolute inset-0 blur-lg bg-cyan-500/30 group-hover:bg-cyan-500/50 transition"></div>
                      </div>
                    </div>
                    <CardTitle className="text-xl text-white group-hover:text-cyan-400 transition mb-2">{project.title}</CardTitle>
                    <div className="flex items-center gap-2 mb-4">
                      <Badge variant="outline" className="text-xs text-cyan-400 border-cyan-500/30">
                        {project.industry}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400 mb-4">{project.description}</p>
                    
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-cyan-400 mb-2">Key Deliverables:</h4>
                      <ul className="space-y-1">
                        {project.deliverables.map((deliverable, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-400">
                            <CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                            <span>{deliverable}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Resources/Blog Section */}
      <section id="resources" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border-cyan-500/30">
              Resources
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">Resources & Insights</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Stay informed about DPDPA and data protection best practices
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {blogLoading ? (
              <div className="col-span-2 text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
                <p className="mt-4 text-gray-400">Loading resources...</p>
              </div>
            ) : blogPosts.length === 0 ? (
              <div className="col-span-2 text-center py-12">
                <p className="text-gray-400">No blog posts available yet.</p>
              </div>
            ) : (
              blogPosts.map((post) => (
                <Card 
                  key={post.id} 
                  onClick={() => {
                    // Save current scroll position
                    sessionStorage.setItem('scrollPosition', window.scrollY.toString());
                    navigate(`/blog/${post.id}`);
                 
                  }}
                  className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] border-cyan-500/20 hover:border-cyan-500/50 transition cursor-pointer group hover:shadow-xl hover:shadow-cyan-500/20"
                >
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-3">
                      <Badge className="bg-gradient-to-r from-purple-500/20 to-cyan-500/20 text-purple-400 border-purple-500/30">
                        {post.category}
                      </Badge>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="h-4 w-4" />
                        {new Date(post.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                    <CardTitle className="text-xl text-white group-hover:text-cyan-400 transition">
                      {post.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400 mb-4">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="h-4 w-4" />
                        {post.readTime}
                      </div>
                      <div className="flex items-center gap-2 text-cyan-400 group-hover:gap-3 transition-all">
                        <span className="text-sm font-medium">Read Article</span>
                        <ArrowRight className="h-5 w-5 group-hover:translate-x-2 transition" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border-cyan-500/30">
              Get In Touch
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-cyan-200 bg-clip-text text-transparent">Let's Work Together</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Get in touch with our team to discuss your data protection needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <Card className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] border-cyan-500/20">
                <CardHeader>
                  <CardTitle className="text-white">Send us a message</CardTitle>
                  <CardDescription className="text-gray-400">We'll respond within 24 hours</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Input
                        name="name"
                        placeholder="Your Name *"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="bg-[#0d1525] border-cyan-500/30 focus:border-cyan-500 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <Input
                        name="email"
                        type="email"
                        placeholder="Email Address *"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-[#0d1525] border-cyan-500/30 focus:border-cyan-500 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <Input
                        name="phone"
                        type="tel"
                        placeholder="Phone Number *"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="bg-[#0d1525] border-cyan-500/30 focus:border-cyan-500 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <Input
                        name="company"
                        placeholder="Company Name"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="bg-[#0d1525] border-cyan-500/30 focus:border-cyan-500 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <Textarea
                        name="message"
                        placeholder="Tell us about your compliance needs *"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={5}
                        className="bg-[#0d1525] border-cyan-500/30 focus:border-cyan-500 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white border-0 shadow-lg shadow-cyan-500/50"
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                      <Send className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <div className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500/50 transition group">
                <div className="flex items-start gap-4">
                  <div className="relative">
                    <div className="h-12 w-12 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-lg flex items-center justify-center relative z-10">
                      <Mail className="h-6 w-6 text-cyan-400" />
                    </div>
                    <div className="absolute inset-0 blur-md bg-cyan-500/30 group-hover:bg-cyan-500/50 transition"></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Email</h3>
                    <a 
                      href={`mailto:${companyInfo.email}`}
                      className="text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer"
                    >
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500/50 transition group">
                <div className="flex items-start gap-4">
                  <div className="relative">
                    <div className="h-12 w-12 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-lg flex items-center justify-center relative z-10">
                      <Phone className="h-6 w-6 text-cyan-400" />
                    </div>
                    <div className="absolute inset-0 blur-md bg-cyan-500/30 group-hover:bg-cyan-500/50 transition"></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Phone</h3>
                    <a 
                      href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                      className="text-gray-400 hover:text-cyan-400 transition-colors cursor-pointer"
                    >
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500/50 transition group">
                <div className="flex items-start gap-4">
                  <div className="relative">
                    <div className="h-12 w-12 bg-gradient-to-br from-green-500/20 to-cyan-500/20 rounded-lg flex items-center justify-center relative z-10">
                      <MessageCircle className="h-6 w-6 text-green-400" />
                    </div>
                    <div className="absolute inset-0 blur-md bg-green-500/30 group-hover:bg-green-500/50 transition"></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">WhatsApp</h3>
                    <a 
                      href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-green-400 transition-colors cursor-pointer block"
                    >
                      {companyInfo.whatsapp}
                    </a>
                    <Button
                      onClick={openWhatsApp}
                      size="sm"
                      className="mt-2 bg-green-600 hover:bg-green-700 text-white text-xs"
                    >
                      Message on WhatsApp
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500/50 transition group">
                <div className="flex items-start gap-4">
                  <div className="relative">
                    <div className="h-12 w-12 bg-gradient-to-br from-purple-500/20 to-cyan-500/20 rounded-lg flex items-center justify-center relative z-10">
                      <MapPin className="h-6 w-6 text-purple-400" />
                    </div>
                    <div className="absolute inset-0 blur-md bg-purple-500/30 group-hover:bg-purple-500/50 transition"></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Location</h3>
                    <a 
                      href="https://goo.gl/maps/DV4gaukRMDXYW9ALA?g_st=aw"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-purple-400 transition-colors cursor-pointer"
                    >
                      {companyInfo.address}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0e1a] border-t border-cyan-500/10 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4">
                <img
                  src="/logo_Lexprive.png"
                  alt="Lexprive Consultants"
                  className="
                      h-7
                      object-contain
                      scale-115
                      sm:scale-100
                      md:scale-150
                      origin-left
                    "
                />
               </div>
              {/* <p className="text-gray-500 text-xs mt-2 text-center">

                {companyInfo.tagline}
              </p> */}
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-white">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-cyan-400 transition-colors">About Us</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-cyan-400 transition-colors">Services</button></li>
                <li><button onClick={() => scrollToSection('case-studies')} className="text-gray-400 hover:text-cyan-400 transition-colors">Case Studies</button></li>
                <li><button onClick={() => scrollToSection('resources')} className="text-gray-400 hover:text-cyan-400 transition-colors">Resources</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-white">Services</h4>
              <ul className="space-y-2 text-sm">
                <li className="text-gray-400">DPDPA Compliance</li>
                <li className="text-gray-400">Privacy Audits</li>
                <li className="text-gray-400">Policy Drafting</li>
                <li className="text-gray-400">Training & Consulting</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-white">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a 
                    href={`mailto:${companyInfo.email}`}
                    className="text-gray-400 hover:text-cyan-400 transition-colors"
                  >
                    {companyInfo.email}
                  </a>
                </li>
                <li>
                  <a 
                    href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                    className="text-gray-400 hover:text-cyan-400 transition-colors"
                  >
                    {companyInfo.phone}
                  </a>
                </li>
                <li>
                  <a 
                    href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-green-400 transition-colors"
                  >
                    {companyInfo.whatsapp}
                  </a>
                </li>
                <li>
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(companyInfo.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-purple-400 transition-colors"
                  >
                    {companyInfo.address}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-cyan-500/10 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} {companyInfo.name}. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button - LEFT SIDE - Real WhatsApp Icon */}
      <a
        href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, '')}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-6 h-16 w-16 bg-[#25D366] hover:bg-[#20BA5A] rounded-full shadow-2xl flex items-center justify-center z-50 transition-all hover:scale-110"
        aria-label="Contact on WhatsApp"
      >
        <img 
          src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" 
          alt="WhatsApp" 
          className="h-10 w-10"
        />
      </a>
    </div>
  );
};

export default Home;
